import { useEffect, useState } from "react";
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Clock3,
  ExternalLink,
  FileCheck2,
  FileText,
  History,
  ListChecks,
  MapPin,
  PackageSearch,
  Save,
  ShieldAlert,
  UserRound,
  Lock,
} from "lucide-react";
import { Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LicitacionExpediente } from "@/components/documentos/LicitacionExpediente";
import { OrdenesCompraCard } from "@/components/licitaciones/OrdenesCompraCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { estadoColors, estadoLabels, formatCLP } from "@/lib/mock-data";
import { formatMontoMoneda, monedaLabel } from "@/lib/monto";
import { buildTenderBrief } from "@/lib/licitacion-analysis";
import { formatTenderDate, mercadoPublicoUrl } from "@/lib/licitaciones";
import { evaluarElegibilidad, puedeMoverAEtapa } from "@/lib/licitacion-elegibilidad";
import { canalBadge } from "@/lib/mercado-publico-catalog";
import {
  AdjudicacionOcdsPanel,
  FuenteDatosBadge,
  OferentesPanel,
  PreguntasRespuestasPanel,
} from "@/components/licitaciones/OcdsPanels";
import {
  CriteriosEvaluacionPanel,
  GarantiasPanel,
  OrigenFichaBadge,
  RequisitosOfertaPanel,
} from "@/components/licitaciones/FichaPortalPanels";
import { cn } from "@/lib/utils";

import { licitacionesService } from "@/services/licitaciones.service";
import type { Licitacion, LicitacionEstado, Prioridad } from "@/types";

const estados = Object.keys(estadoLabels) as LicitacionEstado[];
const prioridades: Prioridad[] = ["alta", "media", "baja"];

export function LicitacionDetail({
  licitacion,
  onChange,
  compact = false,
}: {
  licitacion: Licitacion;
  onChange?: (next: Licitacion) => void;
  compact?: boolean;
}) {
  const [estado, setEstado] = useState<LicitacionEstado>(
    licitacion.tracking?.estadoPipeline ?? "detectada",
  );
  const [prioridad, setPrioridad] = useState<Prioridad>(licitacion.tracking?.prioridad ?? "media");
  const [responsable, setResponsable] = useState(licitacion.tracking?.responsable ?? "");
  const [notas, setNotas] = useState(licitacion.tracking?.notas ?? "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEstado(licitacion.tracking?.estadoPipeline ?? "detectada");
    setPrioridad(licitacion.tracking?.prioridad ?? "media");
    setResponsable(licitacion.tracking?.responsable ?? "");
    setNotas(licitacion.tracking?.notas ?? "");
  }, [licitacion]);

  const save = async (postulada = false) => {
    if (saving) return;
    const etapa: LicitacionEstado = postulada ? "postulada" : estado;
    const regla = puedeMoverAEtapa(licitacion, etapa);
    if (!regla.permitido) {
      toast.error(`No se puede pasar a ${estadoLabels[etapa]}`, { description: regla.motivo });
      return;
    }
    setSaving(true);
    try {
      if (!licitacion.tracking) await licitacionesService.track(licitacion.id);
      const tracking = await licitacionesService.updateTracking(licitacion.id, {
        estadoPipeline: postulada ? "postulada" : estado,
        prioridad,
        responsable: responsable.trim(),
        notas: notas.trim(),
      });
      onChange?.({ ...licitacion, tracking });
      if (postulada) setEstado("postulada");
      toast.success(
        postulada ? "Licitación marcada como postulada" : "Gestión interna actualizada",
      );
    } catch {
      toast.error("No fue posible guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  const management = (
    <ManagementPanel
      licitacion={licitacion}
      estado={estado}
      prioridad={prioridad}
      responsable={responsable}
      notas={notas}
      saving={saving}
      setEstado={setEstado}
      setPrioridad={setPrioridad}
      setResponsable={setResponsable}
      setNotas={setNotas}
      save={save}
      compact={compact}
    />
  );

  return (
    <div className={cn("space-y-5", compact && "space-y-4")}>
      <TenderHeader
        licitacion={licitacion}
        compact={compact}
        onParticipate={() => save()}
        saving={saving}
      />
      {compact ? (
        <div className="space-y-4">
          <ExecutiveBrief licitacion={licitacion} />
          <ItemsTable licitacion={licitacion} />
          <Section title="Próximos hitos" icon={CalendarClock} source="Mercado Público">
            <Timeline licitacion={licitacion} compact />
          </Section>
          <BuyerPanel licitacion={licitacion} />
          <ConditionsPanel licitacion={licitacion} />
          <CompactAdjudication licitacion={licitacion} />
          <PreguntasRespuestasPanel licitacion={licitacion} />
          <OferentesPanel licitacion={licitacion} />
          <OfficialAccess licitacion={licitacion} />
          <DecisionPanel licitacion={licitacion} />
          {management}
        </div>
      ) : (
        <Tabs defaultValue="resumen" className="space-y-5">
          <div className="sticky top-0 z-20 overflow-x-auto border-b border-border bg-background/95 backdrop-blur">
            <TabsList className="h-auto min-w-max justify-start bg-transparent p-0">
              <WorkspaceTab value="resumen" label="Resumen" icon={PackageSearch} />
              <WorkspaceTab value="expediente" label="Expediente" icon={FileText} />
              <WorkspaceTab value="analisis" label="Análisis y plan" icon={ClipboardCheck} />
              <WorkspaceTab value="participacion" label="Consultas y oferentes" icon={Users} />
              <WorkspaceTab value="oficial" label="Datos oficiales" icon={ListChecks} />
            </TabsList>
          </div>

          <TabsContent value="resumen" className="mt-0">
            <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
              <div className="space-y-5">
                <ExecutiveBrief licitacion={licitacion} />
                <ItemsTable licitacion={licitacion} />
                <div className="grid gap-5 lg:grid-cols-2">
                  <Section
                    title="Cronología crítica"
                    icon={CalendarClock}
                    source="Mercado Público"
                    framed
                  >
                    <Timeline licitacion={licitacion} compact />
                  </Section>
                  <BuyerPanel licitacion={licitacion} />
                </div>
                <ConditionsPanel licitacion={licitacion} />
              </div>
              <aside className="space-y-5 xl:sticky xl:top-20">
                <DecisionPanel licitacion={licitacion} />
                {management}
              </aside>
            </div>
          </TabsContent>

          <TabsContent value="expediente" className="mt-0 space-y-5">
            <OfficialAccess licitacion={licitacion} />
            <OrdenesCompraCard codigo={licitacion.codigo} />
            <LicitacionExpediente licitacionId={licitacion.id} codigo={licitacion.codigo} />

            {licitacion.urlActa && (
              <Section title="Adjudicación" icon={UserRound} source="Mercado Público" framed>
                <OfficialLink
                  href={licitacion.urlActa}
                  label="Abrir acta oficial de adjudicación"
                />
              </Section>
            )}
            <div className="flex justify-end">
              <OrigenFichaBadge licitacion={licitacion} />
            </div>
            <div className="grid items-start gap-5 lg:grid-cols-2">
              <CriteriosEvaluacionPanel licitacion={licitacion} />
              <div className="space-y-5">
                <GarantiasPanel licitacion={licitacion} />
                <RequisitosOfertaPanel licitacion={licitacion} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="analisis" className="mt-0">
            <div className="space-y-5">
              <DecisionWorkspace licitacion={licitacion} />
              <div className="grid gap-5 lg:grid-cols-2">
                {management}
                <Section title="Plan de trabajo" icon={ListChecks} source="Interno" framed>
                  <EmptyCapability
                    title="Sin tareas planificadas"
                    description="Usa las notas de gestión para registrar acuerdos mientras se integra la planificación persistente."
                  />
                </Section>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="participacion" className="mt-0 space-y-5">
            <div className="grid items-start gap-5 lg:grid-cols-2">
              <PreguntasRespuestasPanel licitacion={licitacion} />
              <div className="space-y-5">
                <OferentesPanel licitacion={licitacion} />
                <AdjudicacionOcdsPanel licitacion={licitacion} />
              </div>
            </div>
            {!licitacion.tieneOcds && (
              <p className="text-xs text-muted-foreground">
                Esta licitación aún no cruza con el estándar OCDS de Mercado Público; consultas
                y oferentes se completarán en la próxima sincronización.
              </p>
            )}
          </TabsContent>

          <TabsContent value="oficial" className="mt-0 space-y-5">
            <OfficialData licitacion={licitacion} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function TenderHeader({
  licitacion,
  compact,
  onParticipate,
  saving,
}: {
  licitacion: Licitacion;
  compact: boolean;
  onParticipate: () => void;
  saving: boolean;
}) {
  const brief = buildTenderBrief(licitacion);
  const state = licitacion.tracking?.estadoPipeline;
  const elegibilidad = evaluarElegibilidad(licitacion);
  return (
    <section
      className={cn(
        "space-y-4 border-b border-border pb-5",
        !compact && "border bg-card p-5 shadow-elegant sm:p-6",
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <SourceLabel label="Mercado Público" />
        <span
          className={cn(
            "rounded-full px-2 py-0.5 text-xs font-medium",
            state ? estadoColors[state] : "bg-muted text-muted-foreground",
          )}
        >
          {state ? estadoLabels[state] : "Sin seguimiento"}
        </span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
          MP: {licitacion.estadoMP}
        </span>
        {(() => {
          const canal = canalBadge(licitacion.tipoMP);
          if (!canal.canal) return null;
          return (
            <span
              className={cn("rounded-full px-2 py-0.5 text-xs font-medium", canal.color)}
              title={canal.tooltip}
            >
              {canal.label}
              {licitacion.tipoMP ? ` · ${licitacion.tipoMP}` : ""}
            </span>
          );
        })()}
        <FuenteDatosBadge licitacion={licitacion} />

        <span
          className={cn(
            "flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
            elegibilidad.postulable
              ? elegibilidad.estado === "por-vencer"
                ? "bg-warning/15 text-warning"
                : "bg-success/15 text-success"
              : "bg-muted text-muted-foreground",
          )}
          title={elegibilidad.motivo}
        >
          {!elegibilidad.postulable && <Lock className="size-3" />}
          {elegibilidad.postulable
            ? elegibilidad.estado === "por-vencer"
              ? "Cierra pronto"
              : "Admite ofertas"
            : "No admite ofertas"}
        </span>
      </div>
      {!elegibilidad.postulable && (
        <p className="border-l-2 border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
          {elegibilidad.motivo} Solo se permiten acciones de consulta y seguimiento.
        </p>
      )}
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-start">
        <div>
          <p className="font-mono text-xs text-muted-foreground">{licitacion.codigo}</p>
          <h2
            className={cn(
              "font-display font-semibold text-foreground",
              compact ? "text-lg" : "text-2xl",
            )}
          >
            {licitacion.nombre}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {licitacion.organismo}
            {licitacion.organismoUnidad ? ` · ${licitacion.organismoUnidad}` : ""}
          </p>
        </div>
        {!compact && (
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button size="sm" onClick={onParticipate} disabled={saving}>
              {licitacion.tracking ? <Save /> : <CheckCircle2 />}
              {saving
                ? "Guardando…"
                : licitacion.tracking
                  ? "Guardar seguimiento"
                  : elegibilidad.postulable
                    ? "Participar"
                    : "Seguir (solo consulta)"}
            </Button>
            <Button asChild size="sm" variant="outline">
              <a href={mercadoPublicoUrl(licitacion.codigo)} target="_blank" rel="noreferrer">
                Ficha oficial <ExternalLink />
              </a>
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden border border-border bg-border lg:grid-cols-3 xl:grid-cols-6">
        <Metric
          label="Monto estimado"
          value={
            licitacion.montoEstimado == null
              ? "No público"
              : formatMontoMoneda(licitacion.montoEstimado, licitacion.moneda)
          }
        />
        <Metric label="Cierre" value={formatTenderDate(licitacion.fechaCierre, true)} />
        <Metric
          label="Tiempo restante"
          value={
            brief.remainingDays == null
              ? "No informado"
              : brief.remainingDays < 0
                ? "Plazo vencido"
                : brief.remainingDays === 0
                  ? "Cierra hoy"
                  : `${brief.remainingDays} días`
          }
          emphasis={brief.remainingDays != null && brief.remainingDays <= 3}
        />
        <Metric
          label="Reclamos al organismo (12 meses)"
          value={
            licitacion.cantidadReclamos == null
              ? "No informado"
              : licitacion.cantidadReclamos.toLocaleString("es-CL")
          }
        />
        <Metric label="Prioridad" value={licitacion.tracking?.prioridad || "Sin definir"} />
        <Metric label="Responsable" value={licitacion.tracking?.responsable || "Sin asignar"} />
      </div>
    </section>
  );
}

function ExecutiveBrief({ licitacion }: { licitacion: Licitacion }) {
  const brief = buildTenderBrief(licitacion);
  return (
    <Section
      title="Brief de 60 segundos"
      icon={Clock3}
      source="Calculado desde datos oficiales"
      framed
    >
      <div className="grid gap-4 md:grid-cols-[1fr_14rem]">
        <div>
          <p className="text-sm font-semibold text-foreground">
            Próxima acción: {brief.nextAction}
          </p>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {licitacion.descripcion ||
              "Mercado Público no informa una descripción en este registro; revisa las bases oficiales antes de evaluar el alcance."}
          </p>
        </div>
        <div className="border-l border-border pl-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Completitud</span>
            <span className="font-medium capitalize">{brief.quality}</span>
          </div>
          <Progress value={brief.completeness} className="mt-2" />
          <p className="mt-2 text-xs text-muted-foreground">
            {brief.availableFields} de {brief.totalFields} campos clave
          </p>
        </div>
      </div>
      {brief.alerts.length > 0 && (
        <div className="space-y-2 border-t border-border pt-3">
          {brief.alerts.map((alert) => (
            <div key={alert} className="flex gap-2 text-xs text-muted-foreground">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
              <span>{alert}</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function DecisionPanel({ licitacion }: { licitacion: Licitacion }) {
  const score = licitacion.tracking?.scoreIA;
  return (
    <Section
      title="Decisión"
      icon={ClipboardCheck}
      source={score == null ? "Pendiente" : "Interno"}
      framed
    >
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground">Score registrado</p>
          <p className="text-3xl font-semibold tabular-nums">{score == null ? "—" : `${score}%`}</p>
        </div>
        <span className="rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground">
          {score == null
            ? "Sin evaluar"
            : score >= 75
              ? "Buen encaje"
              : score >= 45
                ? "Revisar"
                : "Bajo encaje"}
        </span>
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        El score actual no incluye un desglose verificable. No se presenta como recomendación
        automática hasta contar con perfil, factores y fuentes persistidas.
      </p>
    </Section>
  );
}

function DecisionWorkspace({ licitacion }: { licitacion: Licitacion }) {
  const brief = buildTenderBrief(licitacion);
  const checks = [
    ["Objeto y alcance", Boolean(licitacion.descripcion), "Descripción oficial disponible"],
    ["Encaje económico", licitacion.montoEstimado != null, "Monto estimado informado"],
    [
      "Capacidad de respuesta",
      brief.remainingDays != null && brief.remainingDays >= 4,
      "Al menos 4 días antes del cierre",
    ],
    ["Cobertura territorial", Boolean(licitacion.region), "Región informada"],
    [
      "Trazabilidad del comprador",
      Boolean(licitacion.compradorNombre),
      "Contacto comprador informado",
    ],
  ] as const;
  const passed = checks.filter(([, ok]) => ok).length;
  return (
    <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
      <Section title="Plantilla go / no-go" icon={ClipboardCheck} source="Calculado" framed>
        <div className="flex items-center justify-between border-b border-border pb-4">
          <div>
            <p className="text-sm font-medium">Preparación de la decisión</p>
            <p className="text-xs text-muted-foreground">
              Validaciones basadas solo en datos disponibles
            </p>
          </div>
          <p className="text-2xl font-semibold tabular-nums">
            {passed}/{checks.length}
          </p>
        </div>
        <div className="divide-y divide-border">
          {checks.map(([label, ok, detail]) => (
            <div key={label} className="flex items-start justify-between gap-4 py-3">
              <div>
                <p className="text-sm font-medium">{label}</p>
                <p className="text-xs text-muted-foreground">{detail}</p>
              </div>
              <span
                className={cn(
                  "rounded-md px-2 py-1 text-xs font-medium",
                  ok ? "bg-success/15 text-success" : "bg-warning/15 text-warning",
                )}
              >
                {ok ? "Disponible" : "Revisar"}
              </span>
            </div>
          ))}
        </div>
      </Section>
      <div className="space-y-5">
        <DecisionPanel licitacion={licitacion} />
        <Section title="Riesgos y preguntas" icon={AlertTriangle} source="Calculado" framed>
          {brief.alerts.length ? (
            <ul className="space-y-2 text-sm text-muted-foreground">
              {brief.alerts.map((item) => (
                <li key={item} className="flex gap-2">
                  <span>•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              No se detectan alertas a partir de los campos disponibles. Esto no reemplaza la
              revisión de bases.
            </p>
          )}
        </Section>
      </div>
    </div>
  );
}

function Timeline({ licitacion, compact = false }: { licitacion: Licitacion; compact?: boolean }) {
  const dates = [
    ["Publicación", licitacion.fechaPublicacion],
    ["Inicio de preguntas", licitacion.calendario?.fechaInicio],
    ["Cierre de preguntas", licitacion.calendario?.fechaFinal],
    ["Publicación de respuestas", licitacion.calendario?.fechaPublicacionRespuestas],
    ["Cierre", licitacion.fechaCierre],
    ["Apertura técnica", licitacion.fechaAperturaTecnica],
    ["Apertura económica", licitacion.fechaAperturaEconomica],
    ["Adjudicación estimada", licitacion.fechaAdjudicacionEstimada],
    ["Firma estimada", licitacion.calendario?.fechaEstimadaFirma],
    ["Adjudicación real", licitacion.fechaAdjudicacion],
  ] as const;
  const visibleDates = compact ? dates.filter(([, date]) => date).slice(0, 6) : dates;
  return (
    <ol>
      {visibleDates.map(([label, date], index) => (
        <li key={label} className="grid grid-cols-[1rem_1fr] gap-3">
          <div className="flex flex-col items-center">
            <span
              className={cn(
                "mt-1.5 size-2 rounded-full",
                date ? "bg-primary" : "bg-muted-foreground/40",
              )}
            />
            {index < visibleDates.length - 1 && <span className="h-full w-px bg-border" />}
          </div>
          <div className="pb-4">
            <p className="text-sm font-medium">{label}</p>
            <p className="text-xs text-muted-foreground">
              {date ? formatTenderDate(date, true) : "No informado por Mercado Público"}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function OfficialData({ licitacion }: { licitacion: Licitacion }) {
  return (
    <>
      <Section title="Identificación del proceso" icon={ListChecks} source="Mercado Público" framed>
        <InfoGrid
          items={[
            ["Código", licitacion.codigo],
            ["Estado", licitacion.estadoMP],
            ["Tipo", licitacion.tipoMP],
            ["Moneda", monedaLabel(licitacion.moneda)],
            [
              "Monto estimado",
              licitacion.montoEstimado == null
                ? null
                : formatMontoMoneda(licitacion.montoEstimado, licitacion.moneda),
            ],
            [
              "Reclamos al organismo (12 meses)",
              licitacion.cantidadReclamos?.toString() ?? null,
            ],
            ["Oferentes", licitacion.numOferentes?.toString() ?? null],
          ]}
        />
        <p className="text-xs text-muted-foreground">
          Los reclamos corresponden al total informado por Mercado Público sobre el organismo
          comprador en los últimos 12 meses; no es el subconjunto de reclamos por incumplimiento
          del plazo de pago.
        </p>
      </Section>
      <ItemsTable licitacion={licitacion} />
      <div className="grid gap-5 lg:grid-cols-2">
        <BuyerPanel licitacion={licitacion} />
        <Section
          title="Calendario oficial completo"
          icon={CalendarClock}
          source="Mercado Público"
          framed
        >
          <Timeline licitacion={licitacion} />
        </Section>
      </div>
      <ConditionsPanel licitacion={licitacion} />
      <Section title="Adjudicación" icon={UserRound} source="Mercado Público" framed>
        {licitacion.adjudicaciones?.length ? (
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="p-2">Ítem</th>
                  <th className="p-2">Proveedor</th>
                  <th className="p-2">RUT</th>
                  <th className="p-2 text-right">Cantidad</th>
                  <th className="p-2 text-right">Monto unitario</th>
                </tr>
              </thead>
              <tbody>
                {licitacion.adjudicaciones.map((item) => (
                  <tr
                    key={`${item.correlativo}-${item.rutProveedor}`}
                    className="border-t border-border"
                  >
                    <td className="p-2">{item.correlativo}</td>
                    <td className="p-2 font-medium">{item.nombreProveedor}</td>
                    <td className="p-2 font-mono text-xs">{item.rutProveedor}</td>
                    <td className="p-2 text-right">{item.cantidad}</td>
                    <td className="p-2 text-right font-mono">
                      {item.montoUnitario === 1 ? "$1 simbólico" : formatCLP(item.montoUnitario)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Mercado Público no informa adjudicaciones para esta licitación.
          </p>
        )}
        {licitacion.urlActa && <OfficialLink href={licitacion.urlActa} label="Ver acta oficial" />}
      </Section>
      <Section title="Fuente y trazabilidad" icon={History} source="Sistema" framed>
        <InfoGrid
          items={[
            ["Última sincronización", formatTenderDate(licitacion.syncedAt, true)],
            ["Creación local", formatTenderDate(licitacion.createdAt, true)],
            ["Última modificación", formatTenderDate(licitacion.updatedAt, true)],
          ]}
        />
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <a href={mercadoPublicoUrl(licitacion.codigo)} target="_blank" rel="noreferrer">
              Ficha oficial <ExternalLink />
            </a>
          </Button>
        </div>
      </Section>
    </>
  );
}

function ItemsTable({ licitacion }: { licitacion: Licitacion }) {
  return (
    <Section title="Qué se compra" icon={PackageSearch} source="Mercado Público" framed>
      <p className="text-sm leading-relaxed text-muted-foreground">
        {licitacion.descripcion || "No informado por Mercado Público."}
      </p>
      {licitacion.items?.length ? (
        <div className="overflow-x-auto border border-border">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="bg-muted/40 text-left text-xs text-muted-foreground">
                <th className="p-2">Ítem</th>
                <th className="p-2">Producto / servicio</th>
                <th className="p-2">Categoría UNSPSC</th>
                <th className="p-2 text-right">Cantidad</th>
                <th className="p-2">Unidad</th>
              </tr>
            </thead>
            <tbody>
              {licitacion.items.map((item) => (
                <tr key={item.correlativo} className="border-t border-border align-top">
                  <td className="p-2 font-mono text-xs">{item.correlativo}</td>
                  <td className="p-2">
                    <p className="font-medium">{item.nombreProducto || "No informado"}</p>
                    {item.descripcion && (
                      <p className="mt-1 max-w-xl text-xs text-muted-foreground">
                        {item.descripcion}
                      </p>
                    )}
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                      {item.codigoProducto ?? "Sin código"}
                    </p>
                  </td>
                  <td className="p-2 text-xs">{item.categoria || "No informado"}</td>
                  <td className="p-2 text-right tabular-nums">{item.cantidad ?? "—"}</td>
                  <td className="p-2">{item.unidadMedida || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyCapability
          title="Ítems aún no enriquecidos"
          description="Actualiza esta licitación para obtener productos, cantidades y códigos desde Mercado Público."
        />
      )}
    </Section>
  );
}

function BuyerPanel({ licitacion }: { licitacion: Licitacion }) {
  // La ficha del directorio (Organismo, sincronizada aparte) complementa el
  // dato embebido en esta licitación puntual — se usa como respaldo cuando
  // Mercado Público no trajo el detalle en compradorDetalle.
  const ficha = licitacion.organismoFicha;
  const address = licitacion.compradorDetalle?.direccionUnidad || ficha?.direccion;
  return (
    <Section title="Quién compra" icon={Building2} source="Mercado Público" framed>
      <InfoGrid
        items={[
          ["Organismo", licitacion.organismo],
          ["RUT unidad", licitacion.compradorDetalle?.rutUnidad || ficha?.rut],
          ["Unidad de compra", licitacion.organismoUnidad || ficha?.unidad],
          ["Código unidad", licitacion.compradorDetalle?.codigoUnidad],
          ["Contacto", licitacion.compradorNombre],
          ["Cargo", licitacion.compradorCargo],
          [
            "Ubicación",
            [licitacion.comuna || ficha?.comuna, licitacion.region || ficha?.region]
              .filter(Boolean)
              .join(", ") || null,
          ],
          ["Dirección", address],
        ]}
      />
      {address && (
        <Button asChild variant="link" size="sm" className="px-0">
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank"
            rel="noreferrer"
          >
            <MapPin /> Ver ubicación
          </a>
        </Button>
      )}
    </Section>
  );
}

function ConditionsPanel({ licitacion }: { licitacion: Licitacion }) {
  const c = licitacion.condiciones;
  const duration = c?.duracionContrato
    ? `${c.duracionContrato} ${durationUnit(c.unidadDuracionContrato)}`
    : null;
  return (
    <Section title="Condiciones clave" icon={ShieldAlert} source="Mercado Público" framed>
      <InfoGrid
        items={[
          ["Fuente de financiamiento", c?.fuenteFinanciamiento],
          ["Tipo de pago", c?.tipoPago],
          ["Duración del contrato", duration],
          ["Renovable", booleanLabel(c?.renovable)],
          ["Permite subcontratación", booleanLabel(c?.permiteSubcontratacion)],
          ["Extensión de plazo", booleanLabel(c?.extensionPlazo)],
          ["Responsable de pago", c?.responsablePago],
          ["Responsable del contrato", c?.responsableContrato],
          ["Dirección de entrega", c?.direccionEntrega],
          ["Dirección de visita", c?.direccionVisita],
        ]}
      />
      {c?.prohibicionContratacion && (
        <div className="flex gap-2 border-l-2 border-warning bg-warning/10 p-3 text-xs text-foreground">
          <AlertTriangle className="size-4 shrink-0 text-warning" />
          <div>
            <p className="font-medium">Restricción de contratación</p>
            <p className="mt-1 text-muted-foreground">{c.prohibicionContratacion}</p>
          </div>
        </div>
      )}
    </Section>
  );
}

function CompactAdjudication({ licitacion }: { licitacion: Licitacion }) {
  return (
    <Section title="Adjudicación" icon={UserRound} source="Mercado Público">
      <InfoGrid
        items={[
          ["Oferentes", licitacion.numOferentes?.toString()],
          [
            "Fecha de adjudicación",
            licitacion.fechaAdjudicacion
              ? formatTenderDate(licitacion.fechaAdjudicacion, true)
              : null,
          ],
          [
            "Proveedores adjudicados",
            licitacion.adjudicaciones?.length
              ? licitacion.adjudicaciones.map((item) => item.nombreProveedor).join(", ")
              : null,
          ],
        ]}
      />
      {licitacion.urlActa && <OfficialLink href={licitacion.urlActa} label="Abrir acta oficial" />}
    </Section>
  );
}

function OfficialAccess({ licitacion }: { licitacion: Licitacion }) {
  const modules = [
    "Adjuntos",
    "Preguntas",
    "Historial",
    "Apertura",
    "Cuadro de ofertas",
    "Aclaraciones",
    "Adjudicación",
    "Orden de compra",
    "Certificados y participantes",
  ];
  return (
    <Section title="Expediente oficial" icon={FileText} source="Mercado Público" framed>
      <p className="text-sm text-muted-foreground">
        La API pública de Mercado Público no expone criterios de evaluación, garantías, requisitos
        para contratar ni los anexos de las bases. Esos módulos se consultan directamente en la
        ficha oficial, sin simular contenido.
      </p>
      <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
        {modules.map((label) => (
          <a
            key={label}
            href={mercadoPublicoUrl(licitacion.codigo)}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between gap-3 bg-card p-3 text-sm font-medium hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span>{label}</span>
            <span className="flex items-center gap-1 text-xs font-normal text-muted-foreground">
              Ver oficial <ExternalLink className="size-3" />
            </span>
          </a>
        ))}
      </div>
    </Section>
  );
}

function ManagementPanel({
  licitacion,
  estado,
  prioridad,
  responsable,
  notas,
  saving,
  setEstado,
  setPrioridad,
  setResponsable,
  setNotas,
  save,
  compact,
}: {
  licitacion: Licitacion;
  estado: LicitacionEstado;
  prioridad: Prioridad;
  responsable: string;
  notas: string;
  saving: boolean;
  setEstado: (value: LicitacionEstado) => void;
  setPrioridad: (value: Prioridad) => void;
  setResponsable: (value: string) => void;
  setNotas: (value: string) => void;
  save: (postulada?: boolean) => Promise<void>;
  compact: boolean;
}) {
  const elegibilidad = evaluarElegibilidad(licitacion);
  return (
    <Section title="Gestión interna" icon={FileCheck2} source="Interno" framed={!compact}>
      {!elegibilidad.postulable && (
        <p className="flex items-start gap-2 rounded-md border border-border bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
          <Lock className="mt-0.5 size-3.5 shrink-0" />
          <span>{elegibilidad.motivo} Puedes seguirla para consulta, pero no avanzar etapas de oferta.</span>
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
        <Field label="Estado del pipeline">
          <Select value={estado} onValueChange={(value) => setEstado(value as LicitacionEstado)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {estados.map((item) => (
                <SelectItem
                  key={item}
                  value={item}
                  disabled={!puedeMoverAEtapa(licitacion, item).permitido && item !== estado}
                >
                  {estadoLabels[item]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Prioridad">
          <Select value={prioridad} onValueChange={(value) => setPrioridad(value as Prioridad)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {prioridades.map((item) => (
                <SelectItem key={item} value={item} className="capitalize">
                  {item}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Responsable">
          <Input
            value={responsable}
            maxLength={120}
            onChange={(event) => setResponsable(event.target.value)}
            placeholder="Sin asignar"
          />
        </Field>
      </div>
      <Field label="Notas">
        <Textarea
          value={notas}
          maxLength={4000}
          onChange={(event) => setNotas(event.target.value)}
          placeholder="Acuerdos, riesgos y próximos pasos"
        />
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => save()} disabled={saving}>
          <Save />
          {saving
            ? "Guardando…"
            : licitacion.tracking
              ? "Guardar gestión"
              : elegibilidad.postulable
                ? "Incorporar al pipeline"
                : "Seguir (solo consulta)"}
        </Button>
        {elegibilidad.postulable && estado !== "postulada" && (
          <Button size="sm" variant="outline" onClick={() => save(true)} disabled={saving}>
            <CheckCircle2 />
            Ya postulé
          </Button>
        )}
      </div>
    </Section>
  );
}

function booleanLabel(value: boolean | null | undefined) {
  return value == null ? null : value ? "Sí" : "No";
}
function durationUnit(value: number | null | undefined) {
  return value === 1 ? "días" : value === 2 ? "meses" : value === 3 ? "años" : "";
}

function WorkspaceTab({
  value,
  label,
  icon: Icon,
}: {
  value: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <TabsTrigger
      value={value}
      className="rounded-none border-b-2 border-transparent px-3 py-2 data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none"
    >
      <Icon className="mr-1.5 size-3.5" />
      {label}
    </TabsTrigger>
  );
}
function SourceLabel({ label }: { label: string }) {
  return (
    <span className="rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
      {label}
    </span>
  );
}
function Section({
  title,
  children,
  icon: Icon,
  framed = false,
  source,
}: {
  title: string;
  children: React.ReactNode;
  icon?: React.ComponentType<{ className?: string }>;
  framed?: boolean;
  source?: string;
}) {
  return (
    <section
      className={cn(
        "space-y-3",
        framed ? "border border-border bg-card p-5 shadow-elegant" : "border-t border-border pt-4",
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 font-display text-sm font-semibold text-foreground">
          {Icon && <Icon className="size-4 text-primary" />}
          {title}
        </h3>
        {source && <SourceLabel label={source} />}
      </div>
      {children}
    </section>
  );
}
function Metric({
  label,
  value,
  emphasis = false,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className="min-w-0 bg-card p-3">
      <p className="text-[11px] uppercase text-muted-foreground">{label}</p>
      <p
        className={cn(
          "mt-1 break-words text-sm font-semibold text-foreground",
          emphasis && "text-destructive",
        )}
      >
        {value}
      </p>
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="space-y-1.5 text-xs font-medium text-muted-foreground">
      <span>{label}</span>
      {children}
    </label>
  );
}
function InfoGrid({ items }: { items: Array<[string, string | null | undefined]> }) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {items.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-[11px] uppercase text-muted-foreground">{label}</dt>
          <dd className="mt-0.5 break-words text-sm text-foreground">
            {value || "No informado por Mercado Público"}
          </dd>
        </div>
      ))}
    </dl>
  );
}
function EmptyCapability({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="border border-dashed border-border bg-muted/20 p-5 text-center">
      <FileText className="mx-auto size-6 text-muted-foreground" />
      <p className="mt-2 text-sm font-medium">{title}</p>
      <p className="mx-auto mt-1 max-w-xl text-xs leading-relaxed text-muted-foreground">
        {description}
      </p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
function OfficialLink({ href, label }: { href: string; label: string }) {
  return (
    <Button asChild variant="link" size="sm" className="px-0">
      <a href={href} target="_blank" rel="noreferrer">
        {label} <ExternalLink />
      </a>
    </Button>
  );
}
